<template>
  <div class="catalog-shop">
    <!-- Hero -->
    <div class="shop-hero" :class="{ 'shop-hero--tall': !selectedBuilding && activeTab === 0 }">
      <div class="shop-hero__bg"/>
      <div class="shop-hero__content">
        <div>
          <h3 class="shop-hero__title mb-25">{{ msg('Shop') }}</h3>
          <p class="shop-hero__subtitle mb-0">{{ selectedBuilding ? msg('Browse items and place orders') : msg('Welcome! Pick a building to get started') }}</p>
        </div>
        <div class="shop-hero__cart" v-if="cartCount > 0">
          <feather-icon icon="ShoppingBagIcon" size="20"/>
          <span class="shop-hero__cart-badge">{{ cartCount }}</span>
        </div>
      </div>
      <!-- Decorative shapes -->
      <div class="shop-hero__deco shop-hero__deco--1"/>
      <div class="shop-hero__deco shop-hero__deco--2"/>
      <div class="shop-hero__deco shop-hero__deco--3"/>
    </div>

    <!-- Tabs: Shop + My Orders -->
    <div class="shop-nav mb-2">
      <div class="shop-nav__tabs">
        <button class="shop-nav__tab" :class="{ 'shop-nav__tab--active': activeTab === 0 }" @click="activeTab = 0">
          <feather-icon icon="ShoppingBagIcon" size="16"/>
          <span>{{ msg('Shop') }}</span>
          <span v-if="filteredItems.length && selectedBuilding" class="shop-nav__tab-count">{{ filteredItems.length }}</span>
        </button>
        <button class="shop-nav__tab" :class="{ 'shop-nav__tab--active': activeTab === 1 }" @click="activeTab = 1; loadMyOrders()">
          <feather-icon icon="FileTextIcon" size="16"/>
          <span>{{ msg('My Orders') }}</span>
          <span v-if="myOrders.length" class="shop-nav__tab-count">{{ myOrders.length }}</span>
        </button>
      </div>
    </div>

    <b-tabs v-model="activeTab" content-class="mt-0" nav-class="d-none">
      <!-- ── Shop Tab ── -->
      <b-tab>
        <!-- Toolbar (only when building is selected) -->
        <div v-if="selectedBuilding" class="shop-toolbar mb-2">
          <div class="shop-toolbar__search">
            <feather-icon icon="SearchIcon" size="16" class="shop-toolbar__search-icon"/>
            <b-form-input v-model="search" class="shop-toolbar__search-input" :placeholder="msg('Search products...')" debounce="300"/>
          </div>
          <div class="shop-toolbar__divider"/>
          <div class="shop-toolbar__building">
            <v-select v-model="selectedBuilding" :options="buildings" :reduce="b => b.id" label="name"
                      :placeholder="msg('Select building')" :clearable="false" class="shop-toolbar__building-select">
              <template #selected-option="{ name, address }">
                <feather-icon icon="HomeIcon" size="14" class="mr-50"/>
                <span>{{ name }}</span>
                <small v-if="address" class="text-muted ml-50">{{ address }}</small>
              </template>
              <template #option="{ name, address }">
                <div>{{ name }}</div>
                <small v-if="address" class="text-muted">{{ address }}</small>
              </template>
            </v-select>
          </div>
          <div class="shop-toolbar__results" v-if="!loading">
            <span>{{ filteredItems.length }} {{ filteredItems.length === 1 ? msg('product') : msg('products') }}</span>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="shop-empty-state">
          <b-spinner variant="primary" style="width:3rem;height:3rem"/>
          <p class="mt-1 text-muted mb-0">{{ msg('Loading products...') }}</p>
        </div>

        <!-- Building picker -->
        <div v-else-if="!selectedBuilding" class="shop-pick">
          <div class="shop-pick__main">
            <div class="shop-pick__intro">
              <div class="shop-pick__intro-icon">
                <feather-icon icon="MapPinIcon" size="24"/>
              </div>
              <h4 class="shop-pick__intro-title">{{ msg('Where are you shopping?') }}</h4>
              <p class="shop-pick__intro-sub">{{ msg('Select your building to see available products and pricing') }}</p>
            </div>
            <div class="shop-pick__grid">
              <div v-for="b in buildings" :key="b.id" class="shop-pick__card" @click="selectedBuilding = b.id">
                <div class="shop-pick__card-visual">
                  <feather-icon icon="HomeIcon" size="24"/>
                </div>
                <span class="shop-pick__card-name">{{ b.name }}</span>
                <span v-if="b.address" class="shop-pick__card-addr">{{ b.address }}</span>
                <div class="shop-pick__card-btn">
                  {{ msg('Browse') }}
                  <feather-icon icon="ArrowRightIcon" size="14"/>
                </div>
              </div>
            </div>
          </div>
          <div class="shop-pick__footer">
            <div class="shop-pick__footer-item">
              <feather-icon icon="ShieldIcon" size="16"/>
              <span>{{ msg('Secure Payment') }}</span>
            </div>
            <div class="shop-pick__footer-sep"/>
            <div class="shop-pick__footer-item">
              <feather-icon icon="ZapIcon" size="16"/>
              <span>{{ msg('Instant Confirmation') }}</span>
            </div>
            <div class="shop-pick__footer-sep"/>
            <div class="shop-pick__footer-item">
              <feather-icon icon="FileTextIcon" size="16"/>
              <span>{{ msg('PDF Invoices') }}</span>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="filteredItems.length === 0" class="shop-empty-state">
          <div class="shop-empty-state__icon">
            <feather-icon icon="PackageIcon" size="40"/>
          </div>
          <h5 class="mt-1 mb-50">{{ msg('No products available') }}</h5>
          <p class="text-muted mb-0">{{ msg('There are no items in the catalog right now') }}</p>
        </div>

        <!-- Product Grid -->
        <div v-else class="shop-grid" :class="{ 'shop-grid--has-cart': cartCount > 0 }">
          <div v-for="item in filteredItems" :key="item.id"
               class="shop-card" :class="{ 'shop-card--in-cart': cartQty(item.id) > 0, 'shop-card--oos': item.stock === 0 }">

            <!-- Image -->
            <div class="shop-card__image-wrap">
              <img v-if="item.imageUrls && item.imageUrls.length" :src="getUrl(item.imageUrls[0])"
                   class="shop-card__image" alt=""/>
              <div v-else class="shop-card__no-image">
                <feather-icon icon="PackageIcon" size="30"/>
              </div>
              <button v-if="cartQty(item.id) > 0" class="shop-card__heart" @click="decrementCart(item)">
                <feather-icon icon="ShoppingBagIcon" size="14"/>
                <span>{{ cartQty(item.id) }}</span>
              </button>
              <div class="shop-card__badges">
                <span v-if="item.stock != null && item.stock <= 5 && item.stock > 0" class="shop-badge shop-badge--low">
                  <feather-icon icon="ZapIcon" size="10" class="mr-25"/>{{ item.stock }} {{ msg('left') }}
                </span>
                <span v-if="item.stock === 0" class="shop-badge shop-badge--oos">
                  {{ msg('Sold out') }}
                </span>
              </div>
            </div>

            <!-- Body -->
            <div class="shop-card__body">
              <p v-if="item.description" class="shop-card__desc">{{ item.description }}</p>
              <h6 class="shop-card__name">{{ item.name }}</h6>
              <div class="shop-card__price-row">
                <span class="shop-card__price">{{ formatPrice(item.price, item.currency) }}</span>
                <span v-if="item.stock != null && item.stock > 5" class="shop-card__avail">
                  <feather-icon icon="CheckCircleIcon" size="12" class="mr-25"/>{{ msg('In stock') }}
                </span>
                <span v-else-if="item.stock == null" class="shop-card__avail">
                  <feather-icon icon="CheckCircleIcon" size="12" class="mr-25"/>{{ msg('In stock') }}
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="shop-card__actions">
              <template v-if="cartQty(item.id) > 0">
                <div class="shop-qty">
                  <button class="shop-qty__btn" @click="decrementCart(item)">
                    <feather-icon icon="MinusIcon" size="14"/>
                  </button>
                  <span class="shop-qty__val">{{ cartQty(item.id) }}</span>
                  <button class="shop-qty__btn"
                          :disabled="item.stock != null && cartQty(item.id) >= item.stock"
                          @click="incrementCart(item)">
                    <feather-icon icon="PlusIcon" size="14"/>
                  </button>
                </div>
              </template>
              <button v-else class="shop-add-btn" :disabled="item.stock === 0" @click="addToCart(item)">
                <feather-icon icon="ShoppingCartIcon" size="14" class="mr-50"/>
                {{ item.stock === 0 ? msg('Sold out') : msg('Add to Cart') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Sticky Cart Bar -->
        <transition name="slide-up">
          <div v-if="cartCount > 0" class="shop-cart-bar">
            <div class="shop-cart-bar__inner">
              <div class="shop-cart-bar__info">
                <div class="shop-cart-bar__icon-wrap">
                  <feather-icon icon="ShoppingBagIcon" size="20"/>
                  <span class="shop-cart-bar__count">{{ cartCount }}</span>
                </div>
                <div class="shop-cart-bar__text">
                  <span class="shop-cart-bar__items">{{ cartCount }} {{ cartCount === 1 ? msg('item') : msg('items') }}</span>
                  <span class="shop-cart-bar__total">{{ formatPrice(cartTotal) }}</span>
                </div>
              </div>
              <div class="shop-cart-bar__actions">
                <button class="shop-cart-bar__clear" @click="clearCart">
                  <feather-icon icon="XIcon" size="14" class="mr-25"/>{{ msg('Clear') }}
                </button>
                <button class="shop-cart-bar__checkout" @click="openCheckout">
                  <feather-icon icon="CreditCardIcon" size="16" class="mr-50"/>
                  {{ msg('Checkout') }}
                  <feather-icon icon="ArrowRightIcon" size="16" class="ml-50"/>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </b-tab>

      <!-- ── My Orders Tab ── -->
      <b-tab>

        <div v-if="loadingOrders" class="text-center py-5"><b-spinner variant="primary"/></div>

        <div v-else-if="myOrders.length === 0" class="shop-empty-state">
          <div class="shop-empty-state__icon">
            <feather-icon icon="ShoppingBagIcon" size="40"/>
          </div>
          <h5 class="mt-1 mb-50">{{ msg('No orders yet') }}</h5>
          <p class="text-muted mb-0">{{ msg('Your purchases will appear here') }}</p>
        </div>

        <div v-else class="my-orders">
          <div v-for="order in myOrders" :key="order.id" class="my-order-card">
            <div class="my-order-card__header">
              <div class="my-order-card__header-left">
                <span class="my-order-card__id">#{{ (order.id || '').slice(-6).toUpperCase() }}</span>
                <span class="my-order-card__date">{{ formatDate(order.createdAt) }}</span>
              </div>
              <div class="my-order-card__header-right">
                <b-badge :variant="orderStatusVariant(order.status)">{{ order.status }}</b-badge>
                <button v-if="order.status !== 'PAID'" class="my-order-card__delete" @click="confirmDeleteOrder(order)"
                        v-b-tooltip.hover :title="msg('Delete order')">
                  <feather-icon icon="Trash2Icon" size="14"/>
                </button>
              </div>
            </div>
            <div class="my-order-card__items">
              <div v-for="line in order.items" :key="line.itemId" class="my-order-card__line">
                <span class="my-order-card__line-name">{{ line.name }}</span>
                <span class="my-order-card__line-qty">{{ line.quantity }} &times; {{ formatPrice(line.price) }}</span>
                <span class="my-order-card__line-total">{{ formatPrice(line.price * line.quantity) }}</span>
              </div>
            </div>
            <div class="my-order-card__footer">
              <div class="my-order-card__total">
                <span>{{ msg('Total') }}</span>
                <span class="my-order-card__total-amount">{{ formatPrice(order.total, order.currency) }}</span>
              </div>
              <div class="my-order-card__footer-right">
                <div v-if="order.paidAt" class="my-order-card__paid">
                  <feather-icon icon="CheckCircleIcon" size="13" class="mr-25"/>
                  {{ msg('Paid') }} {{ formatDate(order.paidAt) }}
                </div>
                <button v-if="order.status === 'PAID'" class="my-order-card__pdf" @click="downloadInvoicePdf(order)"
                        v-b-tooltip.hover :title="msg('Download Invoice PDF')">
                  <feather-icon icon="DownloadIcon" size="14"/>
                  <span>PDF</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <catalog-checkout-modal v-model="showCheckout" :cart="cartItems" :building-id="selectedBuilding"
                            @paid="onPaid"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CatalogCheckoutModal from '@/components/modals/catalog/CatalogCheckoutModal.vue'

export default {
  name: 'CatalogShop',
  components: { CatalogCheckoutModal },
  data() {
    return {
      activeTab: 0,
      loading: false,
      loadingOrders: false,
      search: '',
      selectedBuilding: null,
      cart: {},
      showCheckout: false,
      buildings: [],
      myOrders: [],
    }
  },
  computed: {
    ...mapGetters('catalog', ['allItems']),
    filteredItems() {
      let list = this.allItems.filter(i => i.active)
      if (this.search) {
        const q = this.search.toLowerCase()
        list = list.filter(i => (i.name || '').toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q))
      }
      return list
    },
    cartCount() {
      return Object.values(this.cart).reduce((s, q) => s + q, 0)
    },
    cartTotal() {
      return this.cartItems.reduce((s, i) => s + i.price * i.quantity, 0)
    },
    cartItems() {
      return Object.entries(this.cart)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => {
          const item = this.allItems.find(i => i.id === id)
          return item ? { ...item, quantity: qty } : null
        })
        .filter(Boolean)
    },
  },
  watch: {
    selectedBuilding(buildingId) {
      if (buildingId) {
        const bld = this.buildings.find(b => b.id === buildingId)
        const branchId = bld ? bld.buid : null
        this.loadShopItems(branchId)
      }
    },
  },
  created() {
    this.loadBuildings()
  },
  methods: {
    ...mapActions('catalog', { _loadItems: 'loadItems', _loadMyOrders: 'loadMyOrders', _deleteOrder: 'deleteOrder' }),
    ...mapActions('building', { _getAllBuildings: 'getAllBuildings' }),
    async loadBuildings() {
      try {
        const ans = await this._getAllBuildings({ page: 0, amount: 500, filter: {}, sortcolumn: 'company', sortby: 'asc' })
        this.buildings = (ans['Buildings'] || []).map(b => ({
          id: b.id,
          name: b.company || b.name,
          buid: b.buid,
          address: [b.address, b.houseNumber, b.zip, b.location].filter(Boolean).join(', '),
        }))
        if (this.buildings.length === 1) this.selectedBuilding = this.buildings[0].id
      } catch (e) { console.error(e) }
    },
    async loadMyOrders() {
      if (this.myOrders.length > 0 && !this._ordersStale) return
      this.loadingOrders = true
      try {
        this.myOrders = await this._loadMyOrders() || []
        this._ordersStale = false
      } catch (e) { console.error(e) }
      finally { this.loadingOrders = false }
    },
    formatPrice(v, cur) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur || 'EUR' }).format(v || 0)
    },
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    orderStatusVariant(s) {
      const m = { PAID: 'light-success', PENDING: 'light-warning', CANCELLED: 'light-danger', REFUNDED: 'light-info' }
      return m[s] || 'light-secondary'
    },
    cartQty(id) { return this.cart[id] || 0 },
    addToCart(item) {
      const wasEmpty = this.cartCount === 0
      this.$set(this.cart, item.id, 1)
      if (wasEmpty) {
        this.$nextTick(() => {
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
        })
      }
    },
    incrementCart(item) { this.$set(this.cart, item.id, (this.cart[item.id] || 0) + 1) },
    decrementCart(item) {
      const qty = (this.cart[item.id] || 0) - 1
      if (qty <= 0) this.$delete(this.cart, item.id)
      else this.$set(this.cart, item.id, qty)
    },
    clearCart() { this.cart = {} },
    openCheckout() {
      if (!this.selectedBuilding) return
      this.showCheckout = true
    },
    onPaid() {
      this.cart = {}
      this.showCheckout = false
      this._ordersStale = true
      this.$bvToast.toast(this.msg('Your payment was successful and an invoice has been created.'), {
        title: this.msg('Order Confirmed'),
        variant: 'success',
        solid: true,
        autoHideDelay: 5000,
      })
    },
    downloadInvoicePdf(order) {
      const params = new URLSearchParams({
        bearer: encodeURI(localStorage.Bearer),
      })
      const url = window.config.apiUrl + 'catalog/order/' + order.id + '/pdf?' + params.toString()
      window.open(url, '_blank')
    },
    async loadShopItems(branchId) {
      this.loading = true
      try { await this._loadItems({ activeOnly: true, branchId }) }
      finally { this.loading = false }
    },
    confirmDeleteOrder(order) {
      this.$bvModal.msgBoxConfirm(this.msg('Are you sure you want to delete this order?'), {
        title: this.msg('Delete Order'),
        okVariant: 'danger',
        okTitle: this.msg('Delete'),
        cancelTitle: this.msg('Cancel'),
        centered: true,
      }).then(confirmed => {
        if (confirmed) this.deleteOrder(order.id)
      })
    },
    async deleteOrder(orderId) {
      try {
        await this._deleteOrder(orderId)
        this.myOrders = this.myOrders.filter(o => o.id !== orderId)
        this.$bvToast.toast(this.msg('Order has been deleted.'), {
          title: this.msg('Deleted'),
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })
      } catch (e) {
        this.$bvToast.toast(this.msg('Failed to delete order.'), {
          title: this.msg('Error'),
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss">
/* ───── Hero ───── */
.shop-hero {
  position: relative; border-radius: 14px; overflow: hidden; min-height: 90px;
  margin-bottom: 1.25rem; transition: min-height .3s ease;
  &--tall { min-height: 130px; }
  &__bg {
    position: absolute; inset: 0;
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 50%, #ce9ffc 100%);
  }
  &__content {
    position: relative; z-index: 2; padding: 1.25rem 1.75rem; color: #fff;
    display: flex; justify-content: space-between; align-items: center;
  }
  &__title { color: #fff; font-size: 1.3rem; font-weight: 700; }
  &__subtitle { color: rgba(255,255,255,.75); font-size: .88rem; }
  &__cart {
    position: relative; width: 44px; height: 44px; border-radius: 12px;
    background: rgba(255,255,255,.2); display: flex; align-items: center; justify-content: center;
    backdrop-filter: blur(6px);
  }
  &__cart-badge {
    position: absolute; top: -4px; right: -4px; background: #ea5455; color: #fff;
    font-size: .68rem; font-weight: 700; width: 20px; height: 20px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; border: 2px solid #7367f0;
  }
  &__deco {
    position: absolute; border-radius: 50%; z-index: 1;
    &--1 { width: 120px; height: 120px; top: -30px; right: 40px; background: rgba(255,255,255,.06); }
    &--2 { width: 80px; height: 80px; bottom: -20px; right: 140px; background: rgba(255,255,255,.04); }
    &--3 { width: 50px; height: 50px; top: 10px; right: 200px; background: rgba(255,255,255,.08); }
  }
}

/* ───── Custom Tabs ───── */
.shop-nav {
  &__tabs {
    display: inline-flex; gap: 6px; padding: 3px;
    background: #f0f0f5; border-radius: 12px;
  }
  &__tab {
    display: flex; align-items: center; gap: 7px;
    padding: 8px 18px; border-radius: 9px; border: none;
    background: transparent; color: #8e8ea0; cursor: pointer;
    font-weight: 600; font-size: .82rem;
    transition: all .2s ease;
    &:hover { color: #5e5873; background: rgba(255,255,255,.6); }
    &--active {
      background: #fff; color: #7367f0;
      box-shadow: 0 1px 6px rgba(34,41,47,.06);
    }
  }
  &__tab-count {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 20px; height: 20px; padding: 0 5px;
    border-radius: 20px; font-size: .68rem; font-weight: 700;
    background: rgba(115,103,240,.1); color: #7367f0;
    line-height: 1;
  }
  &__tab--active &__tab-count {
    background: #7367f0; color: #fff;
  }
}

/* ───── Toolbar ───── */
.shop-toolbar {
  display: flex; align-items: center; gap: 0;
  background: #fff; border-radius: 12px; padding: 4px 6px;
  box-shadow: 0 1px 3px rgba(34,41,47,.04);
  border: 1px solid #ededf2;
  flex-wrap: wrap;
  &__search {
    position: relative; flex: 1; min-width: 160px;
  }
  &__search-icon {
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #b9b9c3; z-index: 1;
  }
  &__search-input {
    padding-left: 34px; border: none; background: transparent; border-radius: 8px;
    font-size: .85rem; height: 38px;
    &:focus { box-shadow: none; background: #f8f8fc; }
  }
  &__divider {
    width: 1px; height: 20px; background: #e8e8ee; margin: 0 6px; flex-shrink: 0;
    @media (max-width: 767px) { display: none; }
  }
  &__building {
    min-width: 180px; flex: 0 1 260px;
    .vs__dropdown-toggle {
      border: none; background: transparent; padding: 2px 6px; min-height: 38px;
      border-radius: 8px;
      &:hover { background: #f8f8fc; }
    }
    .vs__search { font-size: .84rem; }
    .vs__selected { font-size: .84rem; }
  }
  &__results {
    margin-left: auto; padding: 0 10px; flex-shrink: 0;
    font-size: .75rem; color: #a8a8b8; font-weight: 500;
    white-space: nowrap;
  }
}

/* ───── Empty states ───── */
.shop-empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 4rem 2rem; text-align: center;
  &__icon {
    width: 80px; height: 80px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    background: rgba(115,103,240,.08); color: #7367f0;
  }
}

/* ───── Product Grid ───── */
.shop-grid {
  display: grid; gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  transition: padding-bottom .3s ease;
  @media (min-width: 1400px) { grid-template-columns: repeat(5, 1fr); }
  @media (min-width: 1200px) and (max-width: 1399px) { grid-template-columns: repeat(4, 1fr); }
  @media (min-width: 992px) and (max-width: 1199px) { grid-template-columns: repeat(3, 1fr); }
  &--has-cart { padding-bottom: 80px; }
}

/* ───── Product Card (compact e-commerce) ───── */
.shop-card {
  background: #fff; border-radius: 12px; overflow: hidden; display: flex; flex-direction: column;
  border: 1px solid #f0f0f5;
  box-shadow: 0 1px 3px rgba(34,41,47,.04);
  transition: all .25s cubic-bezier(.4,0,.2,1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(34,41,47,.1), 0 1px 6px rgba(34,41,47,.04);
  }

  &--in-cart {
    border-color: #7367f0;
    box-shadow: 0 0 0 1px #7367f0, 0 6px 18px rgba(115,103,240,.15);
  }

  &--oos {
    opacity: .5;
    pointer-events: none;
    filter: grayscale(.25);
  }

  /* ── Image ── */
  &__image-wrap {
    position: relative; height: 150px; overflow: hidden;
    background: linear-gradient(160deg, #f8f8fc, #eeeff4);
  }
  &__image {
    width: 100%; height: 100%; object-fit: cover;
    transition: transform .4s cubic-bezier(.4,0,.2,1);
  }
  &:hover &__image { transform: scale(1.06); }
  &__no-image {
    width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
    color: #c8c8d4;
  }
  &__heart {
    position: absolute; top: 8px; right: 8px; z-index: 3;
    width: auto; height: 26px; border-radius: 14px; padding: 0 8px;
    background: #7367f0; color: #fff; border: none; cursor: pointer;
    display: flex; align-items: center; gap: 4px;
    font-size: .68rem; font-weight: 700;
    box-shadow: 0 2px 8px rgba(115,103,240,.35);
    transition: all .2s ease;
    &:hover { background: #6358e0; transform: scale(1.05); }
    span { line-height: 1; }
  }
  &__badges {
    position: absolute; top: 8px; left: 8px;
    display: flex; flex-wrap: wrap; gap: 4px; z-index: 2;
  }

  /* ── Body ── */
  &__body {
    padding: .7rem .9rem .4rem; flex: 1;
    display: flex; flex-direction: column;
  }
  &__desc {
    font-size: .7rem; line-height: 1.4; color: #a0a0b4; margin-bottom: 2px;
    display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;
    text-transform: uppercase; letter-spacing: .04em; font-weight: 500;
  }
  &__name {
    font-weight: 700; margin-bottom: 6px; font-size: .9rem; color: #3b3b50;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    line-height: 1.3;
  }
  &__price-row {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: auto; padding-top: 2px;
  }
  &__price {
    font-size: 1.1rem; font-weight: 800; color: #7367f0;
    letter-spacing: -.03em;
  }
  &__avail {
    font-size: .66rem; color: #28c76f; font-weight: 600;
    display: flex; align-items: center;
  }

  /* ── Actions ── */
  &__actions {
    padding: .5rem .9rem .75rem;
  }
}

/* ───── Badges ───── */
.shop-badge {
  font-size: .64rem; font-weight: 700; padding: 3px 8px; border-radius: 16px;
  backdrop-filter: blur(8px); display: inline-flex; align-items: center;
  &--low {
    background: rgba(255,159,67,.92); color: #fff;
    animation: pulse-badge 2s ease-in-out infinite;
  }
  &--oos { background: rgba(234,84,85,.92); color: #fff; }
}
@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: .7; }
}

/* ───── Add to cart button ───── */
.shop-add-btn {
  width: 100%; padding: 8px 12px; border: none; border-radius: 10px;
  background: linear-gradient(135deg, #7367f0, #9e95f5); color: #fff;
  font-weight: 600; font-size: .8rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all .2s ease;
  position: relative; overflow: hidden;
  &::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,.15), transparent);
    opacity: 0; transition: opacity .2s;
  }
  &:hover:not(:disabled) {
    box-shadow: 0 4px 14px rgba(115,103,240,.35);
    transform: translateY(-1px);
    &::before { opacity: 1; }
  }
  &:active:not(:disabled) { transform: translateY(0); box-shadow: none; }
  &:disabled { opacity: .35; cursor: not-allowed; }
}

/* ───── Quantity stepper ───── */
.shop-qty {
  display: flex; align-items: center; justify-content: center; gap: 0;
  background: #f3f2ff; border-radius: 10px; overflow: hidden;
  border: 1px solid rgba(115,103,240,.12);
  &__btn {
    width: 34px; height: 34px; border: none; background: transparent; cursor: pointer;
    display: flex; align-items: center; justify-content: center; color: #7367f0;
    transition: all .15s;
    &:hover:not(:disabled) { background: rgba(115,103,240,.1); }
    &:disabled { opacity: .3; cursor: not-allowed; }
  }
  &__val {
    font-weight: 800; font-size: .95rem; min-width: 36px; text-align: center;
    color: #5e5873; user-select: none;
  }
}

/* ───── Cart bar ───── */
.shop-cart-bar {
  position: fixed; bottom: 0; left: 260px; right: 0; z-index: 1030;
  padding: 0 1rem .75rem;
  @media (max-width: 991.98px) { left: 0; }
  &__inner {
    max-width: 900px; margin: 0 auto; padding: .75rem 1.25rem;
    background: linear-gradient(135deg, #7367f0, #8b82f0); color: #fff;
    border-radius: 14px; box-shadow: 0 -2px 24px rgba(115,103,240,.25);
    display: flex; align-items: center; justify-content: space-between;
  }
  &__info { display: flex; align-items: center; gap: 12px; }
  &__icon-wrap {
    position: relative; width: 40px; height: 40px; border-radius: 10px;
    background: rgba(255,255,255,.15); display: flex; align-items: center; justify-content: center;
  }
  &__count {
    position: absolute; top: -3px; right: -3px; background: #ea5455; color: #fff;
    font-size: .65rem; font-weight: 700; width: 18px; height: 18px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
  }
  &__text { display: flex; flex-direction: column; }
  &__items { font-size: .78rem; opacity: .8; }
  &__total { font-size: 1.15rem; font-weight: 800; letter-spacing: -.02em; }
  &__actions { display: flex; align-items: center; gap: 10px; }
  &__clear {
    border: 1px solid rgba(255,255,255,.3); background: transparent; color: #fff;
    padding: 6px 14px; border-radius: 8px; font-size: .82rem; font-weight: 500; cursor: pointer;
    display: flex; align-items: center; transition: background .15s;
    &:hover { background: rgba(255,255,255,.1); }
  }
  &__checkout {
    border: none; background: #fff; color: #7367f0; padding: 8px 20px; border-radius: 10px;
    font-size: .9rem; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; transition: all .2s;
    &:hover { box-shadow: 0 4px 14px rgba(0,0,0,.15); transform: translateY(-1px); }
  }
}

.slide-up-enter-active, .slide-up-leave-active { transition: transform .3s ease, opacity .3s ease; }
.slide-up-enter, .slide-up-leave-to { transform: translateY(100%); opacity: 0; }

/* ───── Hide default b-tabs nav (replaced by custom) ───── */

/* ───── Building Picker ───── */
.shop-pick {
  display: flex; flex-direction: column; min-height: calc(100vh - 320px);
  &__main { flex: 1; }
  &__intro { text-align: center; padding: 1.5rem 0 1.75rem; }
  &__intro-icon {
    width: 56px; height: 56px; border-radius: 50%; margin: 0 auto .75rem;
    background: linear-gradient(135deg, #7367f0, #9e95f5); color: #fff;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 6px 20px rgba(115,103,240,.25);
  }
  &__intro-title { font-size: 1.15rem; font-weight: 700; color: #3b3b50; margin-bottom: .25rem; }
  &__intro-sub { font-size: .84rem; color: #a0a0b4; max-width: 380px; margin: 0 auto; }
  &__grid {
    display: grid; gap: 1rem; max-width: 720px; margin: 0 auto;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }
  &__card {
    display: flex; flex-direction: column; align-items: center; text-align: center;
    padding: 1.5rem 1.25rem 1.15rem;
    background: #fff; border-radius: 14px; cursor: pointer;
    border: 1.5px solid #f0f0f5;
    box-shadow: 0 2px 8px rgba(34,41,47,.04);
    transition: all .25s ease;
    &:hover {
      border-color: #7367f0;
      box-shadow: 0 8px 28px rgba(115,103,240,.14);
      transform: translateY(-3px);
    }
  }
  &__card-visual {
    width: 56px; height: 56px; border-radius: 14px; margin-bottom: .75rem;
    background: linear-gradient(135deg, rgba(115,103,240,.08), rgba(206,159,252,.08));
    color: #7367f0; display: flex; align-items: center; justify-content: center;
    transition: all .25s;
  }
  &__card:hover &__card-visual {
    background: linear-gradient(135deg, #7367f0, #9e95f5); color: #fff;
    box-shadow: 0 4px 14px rgba(115,103,240,.3);
  }
  &__card-name { font-weight: 700; font-size: .88rem; color: #3b3b50; margin-bottom: .15rem; }
  &__card-addr { font-size: .76rem; color: #a0a0b4; line-height: 1.35; margin-bottom: .75rem; }
  &__card-btn {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: .74rem; font-weight: 600; color: #7367f0;
    padding: 5px 14px; border-radius: 8px;
    background: rgba(115,103,240,.06);
    transition: all .2s;
  }
  &__card:hover &__card-btn { background: #7367f0; color: #fff; }
  /* ── Sticky footer strip ── */
  &__footer {
    display: flex; align-items: center; justify-content: center; gap: 0;
    padding: 1rem 0; margin-top: auto;
    border-top: 1px solid #f0f0f5;
  }
  &__footer-item {
    display: flex; align-items: center; gap: 6px; padding: 0 1.25rem;
    font-size: .76rem; font-weight: 500; color: #a0a0b4;
    svg { width: 15px; height: 15px; }
  }
  &__footer-sep { width: 1px; height: 16px; background: #e8e8ee; flex-shrink: 0; }
}

/* ───── My Orders ───── */
.my-orders { display: flex; flex-direction: column; gap: 1rem; }

.my-order-card {
  background: #fff; border-radius: 14px; overflow: hidden;
  border: 1px solid #f0f0f5;
  box-shadow: 0 1px 6px rgba(34,41,47,.04);
  transition: box-shadow .2s;
  &:hover { box-shadow: 0 4px 16px rgba(34,41,47,.07); }
  &__header {
    display: flex; justify-content: space-between; align-items: center;
    padding: .85rem 1.25rem; background: #fafafe; border-bottom: 1px solid #f0f0f5;
  }
  &__header-left { display: flex; align-items: center; }
  &__header-right { display: flex; align-items: center; gap: 10px; }
  &__id { font-weight: 700; font-size: .92rem; color: #5e5873; margin-right: .75rem; }
  &__date { font-size: .8rem; color: #a8a8b8; }
  &__delete {
    width: 32px; height: 32px; border-radius: 8px; border: none;
    background: transparent; color: #b4b7bd; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all .2s;
    &:hover { background: rgba(234,84,85,.08); color: #ea5455; }
  }
  &__items { padding: .5rem 1.25rem; }
  &__line {
    display: flex; align-items: center; padding: .5rem 0;
    border-bottom: 1px solid #f5f5f5;
    &:last-child { border-bottom: none; }
  }
  &__line-name { flex: 1; font-weight: 600; font-size: .88rem; color: #5e5873; }
  &__line-qty { font-size: .8rem; color: #a8a8b8; margin-right: 1rem; }
  &__line-total { font-weight: 700; font-size: .88rem; color: #5e5873; }
  &__footer {
    display: flex; justify-content: space-between; align-items: center;
    padding: .75rem 1.25rem; border-top: 1px solid #f0f0f5;
  }
  &__total { display: flex; align-items: center; gap: .5rem; }
  &__total-amount { font-size: 1.15rem; font-weight: 800; color: #7367f0; }
  &__footer-right {
    display: flex; align-items: center; gap: 12px;
  }
  &__paid {
    display: flex; align-items: center; font-size: .8rem; color: #28c76f; font-weight: 600;
  }
  &__pdf {
    display: flex; align-items: center; gap: 5px;
    padding: 5px 12px; border-radius: 8px; border: 1px solid #ededf2;
    background: #fff; color: #7367f0; cursor: pointer;
    font-size: .75rem; font-weight: 600;
    transition: all .2s;
    &:hover { background: #7367f0; color: #fff; border-color: #7367f0; }
  }
}

/* ───── Dark mode ───── */
.dark-layout {
  .shop-nav__tabs { background: #1e2a3e; }
  .shop-nav__tab {
    color: #5a6078;
    &:hover { color: #b4b7bd; background: rgba(255,255,255,.04); }
    &--active { background: #283046; color: #7367f0; box-shadow: 0 1px 6px rgba(0,0,0,.15); }
  }
  .shop-nav__tab-count { background: rgba(115,103,240,.15); color: #7367f0; }
  .shop-nav__tab--active .shop-nav__tab-count { background: #7367f0; color: #fff; }
  .shop-toolbar {
    background: #283046; border-color: rgba(255,255,255,.06);
    box-shadow: 0 1px 3px rgba(0,0,0,.15);
  }
  .shop-toolbar__search-input { color: #d0d2d6; &:focus { background: rgba(255,255,255,.04); } }
  .shop-toolbar__search-icon { color: #4b5572; }
  .shop-toolbar__divider { background: rgba(255,255,255,.06); }
  .shop-toolbar__results { color: #5a6078; }
  .shop-toolbar__building .vs__dropdown-toggle { color: #d0d2d6; &:hover { background: rgba(255,255,255,.04); } }
  .shop-empty-state {
    &__icon { background: rgba(115,103,240,.12); color: #7367f0; }
    h5 { color: #d0d2d6; }
  }
  .shop-card {
    background: #283046; border-color: rgba(255,255,255,.06);
    box-shadow: 0 1px 3px rgba(0,0,0,.2);
    &:hover { box-shadow: 0 12px 28px rgba(0,0,0,.3); }
    &--in-cart { border-color: #7367f0; box-shadow: 0 0 0 1px #7367f0, 0 6px 18px rgba(115,103,240,.18); }
    &__image-wrap { background: linear-gradient(160deg, #1e2840, #232e47); }
    &__no-image { color: #4b5572; }
    &__name { color: #e2e4e8; }
    &__desc { color: #5a6078; }
    &__avail { color: #28c76f; }
    &__heart { background: #7367f0; &:hover { background: #6358e0; } }
  }
  .shop-qty {
    background: rgba(115,103,240,.08); border-color: rgba(115,103,240,.12);
    &__btn { color: #7367f0; &:hover:not(:disabled) { background: rgba(115,103,240,.12); } }
    &__val { color: #d0d2d6; }
  }
  .shop-add-btn {
    background: linear-gradient(135deg, #7367f0, #9e95f5);
    &:hover:not(:disabled) { box-shadow: 0 4px 14px rgba(115,103,240,.45); }
  }
  .shop-cart-bar__inner {
    background: linear-gradient(135deg, #4839eb, #6358e0);
    box-shadow: 0 -2px 24px rgba(0,0,0,.4);
  }
  .shop-cart-bar__checkout { background: #fff; color: #4839eb; }
  .shop-pick {
    &__intro-title { color: #d0d2d6; }
    &__intro-sub { color: #5a6078; }
    &__card {
      background: #283046; border-color: rgba(255,255,255,.06);
      box-shadow: 0 2px 6px rgba(0,0,0,.2);
      &:hover { border-color: #7367f0; background: #2d3a52; box-shadow: 0 8px 28px rgba(115,103,240,.18); }
    }
    &__card-visual { background: rgba(115,103,240,.12); }
    &__card:hover .shop-pick__card-visual { background: linear-gradient(135deg, #7367f0, #9e95f5); color: #fff; }
    &__card-name { color: #d0d2d6; }
    &__card-addr { color: #676d7d; }
    &__card-btn { background: rgba(115,103,240,.1); }
    &__card:hover .shop-pick__card-btn { background: #7367f0; color: #fff; }
    &__footer { border-top-color: rgba(255,255,255,.06); }
    &__footer-item { color: #5a6078; }
    &__footer-sep { background: rgba(255,255,255,.06); }
  }
  .my-order-card {
    background: #283046; border-color: rgba(255,255,255,.06);
    box-shadow: 0 1px 6px rgba(0,0,0,.15);
    &:hover { box-shadow: 0 4px 16px rgba(0,0,0,.2); }
    &__header { background: rgba(255,255,255,.02); border-bottom-color: rgba(255,255,255,.06); }
    &__id { color: #d0d2d6; }
    &__date { color: #676d7d; }
    &__delete { color: #4b5572; &:hover { background: rgba(234,84,85,.12); color: #ea5455; } }
    &__pdf { background: #283046; border-color: rgba(255,255,255,.1); color: #7367f0; &:hover { background: #7367f0; color: #fff; border-color: #7367f0; } }
    &__line { border-bottom-color: rgba(255,255,255,.04); }
    &__line-name { color: #d0d2d6; }
    &__line-qty { color: #676d7d; }
    &__line-total { color: #d0d2d6; }
    &__footer { border-top-color: rgba(255,255,255,.06); }
    &__total { color: #d0d2d6; }
  }
}
</style>
